import * as React from "react";

import { cn } from "../lib/utils";

/** A single navigable item. All strings arrive as props (the package is i18n-free). */
export interface SidebarItem {
  /** Stable id passed back to `onNavigate`. */
  id: string;
  /** Visible label + accessible name. */
  label: string;
  /** Optional leading icon (decorative — the label carries the name). */
  icon?: React.ReactNode;
  /** Optional trailing count badge. */
  count?: number | string;
}

/** A labelled group of items under a section header. */
export interface SidebarGroup {
  /** Section header (10px/700 uppercase). Omit for an unlabelled group. */
  label?: string;
  items: SidebarItem[];
}

export interface SidebarProps {
  /** Accessible name for the nav landmark. */
  label: string;
  groups: SidebarGroup[];
  /** Id of the active item — gets the accent card + aria-current. */
  activeId?: string;
  onNavigate: (id: string) => void;
  /** Brand block content (rendered in the deep-teal logo band). */
  logo?: React.ReactNode;
  /** Pinned bottom slot (typically the user row). */
  footer?: React.ReactNode;
  className?: string;
}

/**
 * The Atlas DS Sidebar (audit D2): a fixed 196px rail with a deep-teal logo
 * block, grouped nav, an active item rendered as a card surface with a 3px accent
 * left border + aria-current, optional per-item count badges, and a pinned footer.
 *
 * Standalone by design — the client's own `core/layout/Sidebar.tsx` is restyled
 * in place (collapse behaviour, router coupling, i18n); this is the showcase-ready
 * DS component with no app coupling. Navigation is callback-based; text-only
 * children (XSS control T-09.3-16). Tokens only — no hardcoded colours/radii.
 */
export function Sidebar({
  label,
  groups,
  activeId,
  onNavigate,
  logo,
  footer,
  className,
}: SidebarProps) {
  return (
    <aside
      data-slot="sidebar"
      className={cn(
        "bg-sidebar text-sidebar-foreground border-sidebar-border flex h-full w-[196px] flex-col border-r",
        className,
      )}
    >
      {logo !== undefined && (
        <div
          data-slot="sidebar-logo"
          className="bg-sidebar-primary text-sidebar-primary-foreground flex h-14 items-center px-4 text-[15px] font-bold"
        >
          {logo}
        </div>
      )}

      <nav
        aria-label={label}
        className="flex-1 space-y-5 overflow-y-auto px-2 py-4"
      >
        {groups.map((group, gIndex) => (
          <div key={group.label ?? gIndex} data-slot="sidebar-group">
            {group.label && (
              <p className="text-muted-foreground mb-2 px-2 text-[10px] font-bold tracking-[0.08em] uppercase">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      data-slot="sidebar-item"
                      aria-current={active ? "page" : undefined}
                      onClick={() => onNavigate(item.id)}
                      className={cn(
                        "focus-visible:ring-ring rounded-field flex w-full items-center gap-2 border border-l-[3px] border-transparent border-l-transparent px-2.5 py-2 text-left text-[13px] font-medium transition-colors duration-150 outline-none focus-visible:ring-2 motion-reduce:transition-none",
                        active
                          ? "bg-card border-border border-l-primary font-bold"
                          : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      )}
                    >
                      {item.icon && (
                        <span
                          aria-hidden="true"
                          className="flex size-4 shrink-0 items-center justify-center"
                        >
                          {item.icon}
                        </span>
                      )}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.count !== undefined && (
                        <span
                          data-slot="sidebar-count"
                          className="bg-sidebar-accent text-sidebar-accent-foreground rounded-count px-[7px] py-[3px] text-[10px] font-bold tabular-nums"
                        >
                          {item.count}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {footer !== undefined && (
        <div
          data-slot="sidebar-footer"
          className="border-sidebar-border border-t p-2"
        >
          {footer}
        </div>
      )}
    </aside>
  );
}
